import React, { useContext, useEffect, useCallback, useRef } from "react";

import ListadoContext from "../../context/ListadoContext";

import {
  TableContainer,
  Paper,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
} from "@mui/material";

import TableHeader from "./components/TableHeader";
import TableBodyO from "./components/TableBody";
import TablePaginationO from "./components/TablePaginationO";

import { parseSelectedData } from "../ListadoFunciones";
import "./tabla.scss";

function Tabla() {
  const {
    opcionesListado,
    datos,
    cabeceras,
    useDataStore,
    dispatch,
    loading,
    ordenarPagina,
  } = useContext(ListadoContext);

  const prevDatosRef = useRef(datos);
  const checkAllVisibleRows = useDataStore(
    (state) => state.checkAllVisibleRows,
  );
  const page = useDataStore((state) => state.page);
  const setPage = useDataStore((state) => state.setPage);
  const rowsPerPage = useDataStore((state) => state.rowsPerPage);
  const setRowsPerPage = useDataStore((state) => state.setRowsPerPage);
  const count = useDataStore((state) => state.count);
  const setCount = useDataStore((state) => state.setCount);
  const order = useDataStore((state) => state.order);
  const orderBy = useDataStore((state) => state.orderBy);
  const setOrder = useDataStore((state) => state.setOrder);
  const setOrderBy = useDataStore((state) => state.setOrderBy);
  const getVisibleRows = useDataStore((state) => state.getVisibleRows);
  const globalIndeterminateChecked = useDataStore(
    (state) => state.globalIndeterminateChecked,
  );
  const globalChecked = useDataStore((state) => state.globalChecked);

  if (prevDatosRef.current !== datos) {
    prevDatosRef.current = datos;
  }

  const { paginacion_backend } = opcionesListado;

  const listadoSeleccion = opcionesListado.listado_seleccion === "s";

  const handleChangePage = useCallback(
    (newPage) => {
      setPage(newPage);
    },
    [setPage],
  );

  const handleChangeRowsPerPage = useCallback(
    (value) => {
      setRowsPerPage(value);
    },
    [setRowsPerPage],
  );

  const handleRequestSort = useCallback(
    async (event, property, id_a) => {
      const isAsc = orderBy === property && order === "asc";
      if (paginacion_backend) {
        await ordenarPagina(id_a, isAsc ? "desc" : "asc");
      }
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [ordenarPagina, order, orderBy, paginacion_backend, setOrder, setOrderBy],
  );

  useEffect(() => {
    if (paginacion_backend) {
      setCount(
        paginacion_backend.hasTotal ? Number(paginacion_backend.total) : -1,
      );
      setPage(Number(paginacion_backend.currentPage) - 1);
      setRowsPerPage(Number(paginacion_backend.perPage));
    }
  }, [paginacion_backend, setCount, setPage, setRowsPerPage]);

  let labelDisplayedRows = ({ from, to, count }) => {
    if (opcionesListado.paginacion_backend) {
      const { currentPage, total, perPage, exactTotal } =
        opcionesListado.paginacion_backend;
      const desde = perPage * (currentPage - 1) + 1;
      const hasta = Math.min(currentPage * perPage, total);

      return exactTotal
        ? `${desde}-${hasta} de ${total}`
        : `${desde}-${hasta} de aprox. ${total}`;
    }
    return `${from}-${to} de ${count}`;
  };

  const checkAllCheckboxes = () => {
    checkAllVisibleRows();
    const visibleRows = getVisibleRows();
    visibleRows.forEach((row) => {
      const datosParseados = parseSelectedData(cabeceras, row, opcionesListado);
      dispatch({
        type: "COMPLETE_DATO_SELECCIONADO",
        payload: datosParseados,
        add: row._checked,
      });
    });
  };

  const cabecerasProcesadas = cabeceras
    .sort((a, b) => a.orden - b.orden)
    .filter((cab) => cab.componente !== "hidden")
    .filter((cab) => cab.componente !== "null")
    .filter((cab) => cab.componente !== "mapa")
    .filter((cab) => cab.componente !== "mapa_info")
    .filter((cab) => cab.mostrar !== "n");
  return (
    <TableContainer component={Paper}>
      <Table
        size="small"
        sx={{
          width: "100%",
          /* ===== TD (body) ===== */
          "& .MuiTableCell-body.MuiTableCell-sizeSmall": {
            color: "inherit",
            boxSizing: "border-box",
            fontSize: "0.8rem",
            fontFamily: "inherit",
            fontWeight: "inherit",
            textAlign: "right",
            padding: "2px 6px",
            lineHeight: 1,
          },
          /* ===== TH (header) ===== */
          "& .MuiTableCell-head.MuiTableCell-sizeSmall": {
            textAlign: "left",
            fontWeight: "bold",
            height: "0.8em",
            fontSize: "0.8rem",
            padding: "2px 6px",
            width: "calc(12.5% + 0px)",
            boxSizing: "border-box",
          },
        }}
      >
        {datos && datos.length > 0 ? (
          <>
            <TableHeader
              cabeceras={cabecerasProcesadas}
              handleRequestSort={handleRequestSort}
              listadoSeleccion={listadoSeleccion}
              checkAllCheckboxes={checkAllCheckboxes}
              globalChecked={globalChecked}
              globalIndeterminateChecked={globalIndeterminateChecked}
            />

            <TableBodyO
              cabeceras={cabecerasProcesadas}
              visibleRows={getVisibleRows()}
              opcionesListado={opcionesListado}
              rowsPerPage={rowsPerPage}
              listadoSeleccion={listadoSeleccion}
              loading={loading}
            />

            <TablePaginationO
              cabeceras={cabecerasProcesadas}
              opcionesListado={opcionesListado}
              count={count}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={handleChangeRowsPerPage}
              page={page}
              setPage={handleChangePage}
              labelDisplayedRows={labelDisplayedRows}
              labelRowsPerPage={"Filas:"}
            />
          </>
        ) : (
          <>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={cabecerasProcesadas.length}
                  align="center"
                  style={{ textAlign: "center" }}
                >
                  {opcionesListado.mensaje_sin_datos ?? "Sin Resultados"}
                </TableCell>
              </TableRow>
            </TableBody>
          </>
        )}
      </Table>
    </TableContainer>
  );
}

export default React.memo(Tabla);
